/**
 * Multi-mission model for command dashboard (tabs + migrate legacy single-mission).
 */
import { liveMissionMemberKeys } from './deviceIdentity';
import { missionIdInAllowList, missionIdListsEquivalent } from './missionPersist';

export function newMissionId() {
  return 'm_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 6);
}

export function defaultMissionName(existing = []) {
  const used = new Set((existing || []).map((m) => String(m?.name || '').trim()));
  let n = 1;
  while (used.has('\u4efb\u52a1 ' + n)) n += 1;
  return '\u4efb\u52a1 ' + n;
}

/** 临时看板分组；commsLinked 缺省 false（通联 opt-in） */
export function normalizeMissionGroup(raw) {
  const g = raw && typeof raw === 'object' ? raw : {};
  const memberIds = Array.isArray(g.memberIds)
    ? g.memberIds.map((x) => String(x || '').trim()).filter(Boolean)
    : [];
  return {
    ...g,
    id: String(g.id || '').trim() || ('g_' + Date.now().toString(36)),
    name: String(g.name || '').trim() || '\u4e34\u65f6\u7ec4',
    collapsed: Boolean(g.collapsed),
    memberIds,
    commsLinked: g.commsLinked === true,
    recordTracks: Object.prototype.hasOwnProperty.call(g, 'recordTracks')
      ? g.recordTracks === true
      : true
  };
}

export function normalizeMission(raw, index = 0) {
  const id = String(raw?.id || '').trim() || newMissionId();
  const name = String(raw?.name || '').trim() || ('\u4efb\u52a1 ' + (index + 1));
  const memberIds = Array.isArray(raw?.memberIds)
    ? raw.memberIds.map((x) => String(x || '').trim()).filter(Boolean)
    : [];
  const rawGroups = Array.isArray(raw?.groups)
    ? raw.groups
    : (Array.isArray(raw?.missionGroups) ? raw.missionGroups : []);
  const groups = rawGroups.map((g) => normalizeMissionGroup(g));
  const hasRt = raw && Object.prototype.hasOwnProperty.call(raw, 'recordTracks');
  return { id, name, memberIds, groups, recordTracks: hasRt ? raw.recordTracks === true : true };
}

export function createEmptyMission(existing = [], name) {
  const list = Array.isArray(existing) ? existing : [];
  return {
    id: newMissionId(),
    name: name || defaultMissionName(list),
    memberIds: [],
    groups: [],
    recordTracks: true
  };
}

/**
 * Migrate user_settings / state: missions[] preferred; else wrap legacy missionMemberIds/groups.
 * @returns {{ missions: object[], activeMissionId: string|null }}
 */
export function migrateLegacyMissions({
  missions,
  activeMissionId,
  missionMemberIds,
  missionGroups,
  missionMode
} = {}) {
  const rawList = Array.isArray(missions) ? missions : null;
  if (rawList && rawList.length > 0) {
    const list = rawList.map((m, i) => normalizeMission(m, i));
    let aid = String(activeMissionId || '').trim();
    if (!aid || !list.some((m) => m.id === aid)) {
      aid = list[0].id;
    }
    return { missions: list, activeMissionId: aid };
  }

  const ids = Array.isArray(missionMemberIds)
    ? missionMemberIds.map((x) => String(x || '').trim()).filter(Boolean)
    : [];
  const groups = Array.isArray(missionGroups) ? missionGroups : [];
  const hasLegacy = ids.length > 0 || groups.length > 0 || Boolean(missionMode);
  if (!hasLegacy) {
    return { missions: [], activeMissionId: null };
  }
  const one = normalizeMission({
    id: newMissionId(),
    name: '\u4efb\u52a1 1',
    memberIds: ids,
    groups
  }, 0);
  return { missions: [one], activeMissionId: one.id };
}

export function getActiveMission(missions, activeMissionId) {
  const list = Array.isArray(missions) ? missions : [];
  if (!list.length) return null;
  const aid = String(activeMissionId || '').trim();
  return list.find((m) => m.id === aid) || list[0] || null;
}

export function uniqueLiveIdsAcrossMissions(missions = [], liveMembers = []) {
  const all = [];
  (Array.isArray(missions) ? missions : []).forEach((m) => {
    (m?.memberIds || []).forEach((id) => all.push(id));
  });
  return liveMissionMemberKeys(all, liveMembers);
}

export function countUniqueLiveAcrossMissions(missions = [], liveMembers = []) {
  return uniqueLiveIdsAcrossMissions(missions, liveMembers).length;
}

export function liveCountForMission(mission, liveMembers = []) {
  return liveMissionMemberKeys(mission?.memberIds || [], liveMembers).length;
}

/** True if memberId appears in any mission other than excludeMissionId. */
export function memberInOtherMissions(memberId, missions, excludeMissionId) {
  const mid = String(memberId || '').trim();
  if (!mid) return false;
  return (Array.isArray(missions) ? missions : []).some((m) => {
    if (!m || m.id === excludeMissionId) return false;
    return missionIdInAllowList(mid, m.memberIds || []);
  });
}

export function replaceMission(missions, missionId, patch) {
  const list = Array.isArray(missions) ? missions : [];
  return list.map((m) => (m.id === missionId ? { ...m, ...patch, id: m.id } : m));
}

export function removeMission(missions, missionId) {
  return (Array.isArray(missions) ? missions : []).filter((m) => m.id !== missionId);
}

export { missionIdListsEquivalent };

/**
 * Mission or any group has recordTracks on.
 */
export function missionWantsRecordTracks(mission) {
  if (!mission) return false;
  if (mission.recordTracks === true) return true;
  return (mission.groups || []).some((g) => g && g.recordTracks === true);
}

/**
 * Member ids that should auto-draw / dense-upload under this mission.
 * Mission-level recordTracks => all memberIds; else union of groups with recordTracks.
 */
export function membersInRecordTrackScope(mission) {
  if (!mission) return [];
  const seen = new Set();
  const out = [];
  const push = (id) => {
    const s = String(id || '').trim();
    if (!s || seen.has(s)) return;
    seen.add(s);
    out.push(s);
  };
  if (mission.recordTracks === true) {
    (mission.memberIds || []).forEach(push);
    return out;
  }
  (mission.groups || []).forEach((g) => {
    if (g && g.recordTracks === true) {
      (g.memberIds || []).forEach(push);
    }
  });
  return out;
}